import timetableSevice from 'ApiService/Admin/timetableSevice';
import classService from 'ApiService/Teacher/Class';
import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const Index = ({ classData, selectedBranch }) => {
  const [loading, setLoading] = useState(true);
  const [batchDetails, setBatchDetails] = useState([]);
  const [filter, setFilter] = useState('all');

  const { state } = useContext(AuthContext);
  const navigate = useNavigate();

  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  // =============================
  // Helpers
  // =============================
  const formatBranchNames = (branchNames = '') =>
    branchNames
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part && part !== 'Awdiz Institute')
      .join(', ');

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString() : '-';

  // =============================
  // Fetch Batch Details (FIXED)
  // =============================
  const fetchBatchDetails = useCallback(async () => {
    if (!classData?.batchId && state.user.role !== 'teacher') return;

    setLoading(true);
    try {
      let response;

      if (state.user.role === 'teacher') {
        response = await classService.getBatchDetails(classData.courseId);
      } else {
        response = await timetableSevice.getBatchesByStructure(
          classData.batchId,
          selectedBranch
        );
      }

      if (response?.data?.success) {
        setBatchDetails(response.data.batches || []);
      } else {
        toast.error('Failed to fetch batch details');
        setBatchDetails([]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch batch details'
      );
      setBatchDetails([]);
    } finally {
      setLoading(false);
    }
  }, [
    state.user.role,
    classData?.courseId,
    classData?.batchId,
    selectedBranch,
  ]);

  // ✅ ESLINT SAFE
  useEffect(() => {
    fetchBatchDetails();
  }, [fetchBatchDetails]);

  // =============================
  // Filter + Sort (ONGOING FIRST)
  // =============================
  const today = new Date();

  const filteredBatches = batchDetails
    .filter((batch) => {
      const endDate = batch?.endDate ? new Date(batch.endDate) : null;
      if (filter === 'completed') return endDate && endDate < today;
      if (filter === 'nonCompleted') return !endDate || endDate >= today;
      return true;
    })
    .sort((a, b) => {
      const aCompleted = a?.endDate && new Date(a.endDate) < today;
      const bCompleted = b?.endDate && new Date(b.endDate) < today;
      return Number(aCompleted) - Number(bCompleted);
    });

  // =============================
  // Loading UI
  // =============================
  if (loading) {
    return (
      <Card extra="w-full p-4">
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
        </div>
      </Card>
    );
  }

  // =============================
  // UI
  // =============================
  return (
    <Card extra="w-full p-4">
      {/* Filters */}
      <div className="mb-4 flex gap-2">
        {[
          { key: 'all', label: 'All Batches' },
          { key: 'completed', label: 'Completed' },
          { key: 'nonCompleted', label: 'Ongoing' },
        ].map((btn) => (
          <button
            key={btn.key}
            onClick={() => setFilter(btn.key)}
            className={`rounded px-4 py-2 font-medium ${
              filter === btn.key
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-800'
            }`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] border-collapse overflow-hidden rounded-lg bg-white shadow dark:bg-navy-700">
          <thead>
            <tr className="bg-gray-100 text-gray-700 dark:bg-navy-600 dark:text-gray-200">
              <th className="border-b border-r p-3 text-center">Batch</th>
              <th className="border-b border-r p-3 text-center">Mode</th>
              <th className="border-b border-r p-3 text-center">
                Course / Structure
              </th>
              <th className="border-b border-r p-3 text-center">Time</th>
              <th className="border-b border-r p-3 text-center">Days</th>
              <th className="border-b border-r p-3 text-center">Instructor</th>
              <th className="border-b p-3 text-center">Status</th>
            </tr>
          </thead>

          <tbody>
            {filteredBatches.map((batch) => {
              const isCompleted =
                batch?.endDate && new Date(batch.endDate) < today;

              return (
                <tr
                  key={batch._id}
                  className="border-b hover:bg-blue-50 dark:hover:bg-navy-600"
                >
                  <td className="border-r p-3 text-center">
                    <div className="font-semibold">
                      Batch {batch.batchNumber}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatBranchNames(batch.branchNames)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDate(batch.startDate)} →{' '}
                      {formatDate(batch.endDate)}
                    </div>
                  </td>

                  <td className="border-r p-3 text-center">{batch.mode}</td>

                  <td className="border-r p-3">
                    <div className="font-medium">
                      {batch.batchStructure?.name}
                    </div>
                    {batch.courses?.map((course) => (
                      <div key={course.id} className="text-sm">
                        {course.name}
                      </div>
                    ))}
                  </td>

                  <td className="border-r p-3">
                    {batch.daySchedules?.map((s) => (
                      <div key={s._id}>
                        {s.day.slice(0, 3)}: {s.startTime} - {s.endTime}
                      </div>
                    ))}
                  </td>

                  <td className="border-r p-3">
                    {daysOfWeek.map((day) => {
                      const active = batch.daySchedules?.some(
                        (d) => d.day === day
                      );
                      return (
                        <span
                          key={day}
                          className={`mr-1 rounded px-2 py-1 text-xs ${
                            active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-400'
                          }`}
                        >
                          {day.slice(0, 3)}
                        </span>
                      );
                    })}
                  </td>

                  <td className="border-r p-3 text-center">
                    {batch.teacher?.name || 'N/A'}
                  </td>

                  <td className="p-3 text-center">
                    <span
                      className={`rounded px-2 py-1 text-xs ${
                        isCompleted
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {isCompleted ? 'Completed' : 'Ongoing'}
                    </span>
                  </td>
                </tr>
              );
            })}

            {filteredBatches.length === 0 && (
              <tr>
                <td colSpan="7" className="p-6 text-center text-gray-500">
                  No batch details available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {state?.user?.role === 'admin' && (
        <div className="mt-4 text-center text-sm text-gray-600">
          <b>Note:</b>{' '}
          <span
            className="cursor-pointer text-blue-500 underline"
            onClick={() => navigate('/admin/view/courses')}
          >
            click here
          </span>{' '}
          to edit batch timing and days.
        </div>
      )}
    </Card>
  );
};

export default Index;
